import { useState, useRef, type ChangeEvent } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';

export interface ImageUploadProps {
  label?: string;
  value?: string | null;
  onChange?: (file: File | null) => void;
  onUpload?: (file: File) => Promise<string>;
  onRemove?: () => void;
  accept?: string;
  maxSize?: number; // in bytes
  error?: string;
  hint?: string;
  disabled?: boolean;
  variant?: 'avatar' | 'rectangle';
  className?: string;
}

export function ImageUpload({
  label,
  value,
  onChange,
  onUpload,
  onRemove,
  accept = 'image/jpeg,image/png,image/gif,image/webp',
  maxSize = 5 * 1024 * 1024, // 5MB default
  error,
  hint,
  disabled = false,
  variant = 'rectangle',
  className = '',
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayError = error || localError;

  const handleClick = () => {
    if (!disabled && !isUploading) {
      inputRef.current?.click();
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input
    e.target.value = '';

    // Validate file type
    if (!accept.includes(file.type)) {
      setLocalError('サポートされていないファイル形式です');
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / 1024 / 1024);
      setLocalError(`ファイルサイズは${maxSizeMB}MB以下にしてください`);
      return;
    }

    setLocalError(null);

    // Create local preview
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    // Call onChange if provided
    onChange?.(file);

    // Call onUpload if provided
    if (onUpload) {
      try {
        setIsUploading(true);
        await onUpload(file);
      } catch (uploadError) {
        console.error('Upload error:', uploadError);
        setLocalError('アップロードに失敗しました');
        setPreviewUrl(null);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewUrl(null);
    setLocalError(null);
    onChange?.(null);
    onRemove?.();
  };

  const displayUrl = previewUrl || value;

  const containerClasses = variant === 'avatar'
    ? 'w-32 h-32 rounded-full'
    : 'w-full aspect-video rounded-lg';

  return (
    <div className={`${className}`}>
      {label && (
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          {label}
        </label>
      )}
      
      <div
        onClick={handleClick}
        className={`
          relative overflow-hidden border-2 border-dashed
          transition-all duration-200 cursor-pointer
          ${containerClasses}
          ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:border-copper hover:bg-copper/5'}
          ${displayError ? 'border-red-500' : 'border-neutral-300'}
        `}
      >
        {displayUrl ? (
          <>
            <img
              src={displayUrl}
              alt="Preview"
              className={`w-full h-full object-cover ${variant === 'avatar' ? 'rounded-full' : ''}`}
            />
            {!disabled && !isUploading && (
              <button
                onClick={handleRemove}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 transition"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {isUploading && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-neutral-400">
            {isUploading ? (
              <>
                <Loader2 className="w-10 h-10 animate-spin mb-2" />
                <span className="text-sm">アップロード中...</span>
              </>
            ) : (
              <>
                {variant === 'avatar' ? (
                  <ImageIcon className="w-10 h-10 mb-2" />
                ) : (
                  <Upload className="w-10 h-10 mb-2" />
                )}
                <span className="text-sm">クリックして選択</span>
              </>
            )}
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        disabled={disabled || isUploading}
        className="hidden"
      />

      {displayError && (
        <p className="mt-2 text-sm text-red-500">{displayError}</p>
      )}
      {hint && !displayError && (
        <p className="mt-2 text-sm text-neutral-500">{hint}</p>
      )}
    </div>
  );
}

// Multi-image upload component
export interface MultiImageUploadProps {
  label?: string;
  images: string[];
  maxImages?: number;
  onUpload: (file: File) => Promise<string>;
  onRemove: (imageUrl: string) => void;
  accept?: string;
  maxSize?: number;
  error?: string;
  hint?: string;
  disabled?: boolean;
  className?: string;
}

export function MultiImageUpload({
  label,
  images,
  maxImages = 5,
  onUpload,
  onRemove,
  accept = 'image/jpeg,image/png,image/gif,image/webp',
  maxSize = 5 * 1024 * 1024,
  error,
  hint,
  disabled = false,
  className = '',
}: MultiImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayError = error || localError;
  const canAddMore = images.length < maxImages && !disabled && !isUploading;

  const handleClick = () => {
    if (canAddMore) {
      inputRef.current?.click();
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = '';

    if (!accept.includes(file.type)) {
      setLocalError('サポートされていないファイル形式です');
      return;
    }

    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / 1024 / 1024);
      setLocalError(`ファイルサイズは${maxSizeMB}MB以下にしてください`);
      return;
    }

    setLocalError(null);

    try {
      setIsUploading(true);
      await onUpload(file);
    } catch (uploadError) {
      console.error('Upload error:', uploadError);
      setLocalError('アップロードに失敗しました');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          {label}
          <span className="text-neutral-400 ml-2">({images.length}/{maxImages})</span>
        </label>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
        {images.map((imageUrl, index) => (
          <div
            key={imageUrl}
            className="relative aspect-square rounded-lg overflow-hidden border border-neutral-200"
          >
            <img
              src={imageUrl}
              alt={`Image ${index + 1}`}
              className="w-full h-full object-cover"
            />
            {!disabled && (
              <button
                onClick={() => onRemove(imageUrl)}
                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full shadow hover:bg-red-600 transition"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}

        {canAddMore && (
          <div
            onClick={handleClick}
            className="aspect-square rounded-lg border-2 border-dashed border-neutral-300 flex items-center justify-center cursor-pointer hover:border-copper hover:bg-copper/5 transition"
          >
            {isUploading ? (
              <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
            ) : (
              <Upload className="w-6 h-6 text-neutral-400" />
            )}
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        disabled={disabled || isUploading}
        className="hidden"
      />

      {displayError && (
        <p className="mt-2 text-sm text-red-500">{displayError}</p>
      )}
      {hint && !displayError && (
        <p className="mt-2 text-sm text-neutral-500">{hint}</p>
      )}
    </div>
  );
}
